import { Component } from '@theme/component';

const YT_ORIGIN = 'https://www.youtube-nocookie.com';
const VIMEO_ORIGIN = 'https://player.vimeo.com';

class SimucubeVideoReviews extends Component {
  requiredRefs = ['grid'];

  #wiredVideos = new WeakSet();

  #playedEmbeds = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    this.refs.grid.addEventListener('click', this.#onClick);
    window.addEventListener('message', this.#onMessage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.grid.removeEventListener('click', this.#onClick);
    window.removeEventListener('message', this.#onMessage);
  }

  #onClick = (event) => {
    const unmute = event.target.closest('[data-unmute]');
    if (unmute && this.refs.grid.contains(unmute)) {
      const card = unmute.closest('[data-video-card]');
      if (card) this.#unmute(card);
      return;
    }
    const overlay = event.target.closest('[data-overlay]');
    if (!overlay || !this.refs.grid.contains(overlay)) return;
    const card = overlay.closest('[data-video-card]');
    if (card) this.#play(card);
  };

  #play(card) {
    this.#pauseOthers(card);
    switch (card.dataset.provider) {
      case 'shopify':
        this.#playNative(card);
        break;
      case 'youtube':
      case 'vimeo':
        this.#playEmbed(card);
        break;
    }
  }

  #playNative(card) {
    const video = card.querySelector('video');
    if (!video) return;

    if (!this.#wiredVideos.has(video)) {
      this.#wiredVideos.add(video);
      video.addEventListener('pause', () => {
        if (!video.ended) this.#showOverlay(card);
      });
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.controls = false;
        this.#showOverlay(card);
      });
      video.addEventListener('play', () => this.#hideOverlay(card));
    }

    video.controls = true;
    video.muted = false;
    this.#hideOverlay(card);
    video.play().catch(() => this.#showOverlay(card));
  }

  #playEmbed(card) {
    const provider = card.dataset.provider;
    const frame = card.querySelector('[data-frame]');
    if (!frame) return;
    let iframe = frame.querySelector('iframe');

    const startMuted = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (!iframe) {
      const id = encodeURIComponent(card.dataset.videoId || '');
      if (!id) return;
      iframe = document.createElement('iframe');
      if (provider === 'youtube') {
        iframe.src = `${YT_ORIGIN}/embed/${id}?autoplay=1&playsinline=1&rel=0&enablejsapi=1${startMuted ? '&mute=1' : ''}`;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      } else {
        iframe.src = `${VIMEO_ORIGIN}/video/${id}?autoplay=1&api=1${startMuted ? '&muted=1' : ''}`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      }
      if (startMuted) card.classList.add('is-muted');
      iframe.allowFullscreen = true;
      iframe.title = card.querySelector('[data-overlay]')?.getAttribute('aria-label') || 'Video review';
      iframe.addEventListener('load', () => this.#subscribe(iframe, provider));
      frame.append(iframe);
    } else {
      const message =
        provider === 'youtube'
          ? JSON.stringify({ event: 'command', func: 'playVideo', args: [] })
          : JSON.stringify({ method: 'play' });
      const origin = provider === 'youtube' ? YT_ORIGIN : VIMEO_ORIGIN;
      iframe.contentWindow?.postMessage(message, origin);
    }

    this.#hideOverlay(card);
  }

  #pauseOthers(current) {
    for (const card of this.refs.grid.querySelectorAll('[data-video-card]')) {
      if (card === current || !card.classList.contains('is-playing')) continue;
      const video = card.querySelector('video');
      if (video && !video.paused) {
        video.pause();
        continue;
      }
      const iframe = card.querySelector('[data-frame] iframe');
      if (iframe?.contentWindow) {
        if (card.dataset.provider === 'youtube') {
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), YT_ORIGIN);
        } else {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), VIMEO_ORIGIN);
        }
      }
      this.#showOverlay(card);
    }
  }

  #unmute(card) {
    const iframe = card.querySelector('[data-frame] iframe');
    if (iframe?.contentWindow) {
      if (card.dataset.provider === 'youtube') {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'unMute', args: [] }), YT_ORIGIN);
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'setVolume', args: [100] }), YT_ORIGIN);
      } else {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), VIMEO_ORIGIN);
      }
    }
    card.classList.remove('is-muted');
  }

  #subscribe(iframe, provider) {
    const target = iframe.contentWindow;
    if (!target) return;
    if (provider === 'youtube') {
      target.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), YT_ORIGIN);
    } else {
      target.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), VIMEO_ORIGIN);
      target.postMessage(JSON.stringify({ method: 'addEventListener', value: 'pause' }), VIMEO_ORIGIN);
      target.postMessage(JSON.stringify({ method: 'addEventListener', value: 'finish' }), VIMEO_ORIGIN);
    }
  }

  #onMessage = (event) => {
    if (event.origin !== YT_ORIGIN && event.origin !== VIMEO_ORIGIN) return;

    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    if (!data || typeof data !== 'object') return;

    const iframe = this.#iframeForSource(event.source);
    if (!iframe) return;
    const card = iframe.closest('[data-video-card]');
    if (!card) return;

    if (event.origin === VIMEO_ORIGIN) {
      if (data.event === 'ready') {
        this.#subscribe(iframe, 'vimeo');
      } else if (data.event === 'play') {
        this.#playedEmbeds.add(card);
        this.#hideOverlay(card);
      } else if (data.event === 'pause') {
        if (this.#playedEmbeds.has(card)) this.#showOverlay(card);
      } else if (data.event === 'finish') {
        this.#playedEmbeds.delete(card);
        iframe.remove();
        this.#showOverlay(card);
      }
    } else {
      const state = data.info?.playerState;
      if (state === 1) {
        this.#playedEmbeds.add(card);
        this.#hideOverlay(card);
      } else if (state === 2) {
        if (this.#playedEmbeds.has(card)) this.#showOverlay(card);
      } else if (state === 0) {
        this.#playedEmbeds.delete(card);
        iframe.remove();
        this.#showOverlay(card);
      }
    }
  };

  #iframeForSource(source) {
    for (const iframe of this.refs.grid.querySelectorAll('[data-frame] iframe')) {
      if (iframe.contentWindow === source) return iframe;
    }
    return null;
  }

  #hideOverlay(card) {
    card.classList.add('is-playing');
  }

  #showOverlay(card) {
    card.classList.remove('is-playing');
  }
}

if (!customElements.get('simucube-video-reviews')) {
  customElements.define('simucube-video-reviews', SimucubeVideoReviews);
}
